import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { getFilteredDevicesRouteConfig } from 'src/app/shared/constants/devicesRouteConfig.constants';
import { Observable, of, forkJoin } from 'rxjs';
import { CalibrationEquipment } from 'src/app/models/calibrationEquipment.model';
import { CalibrationEquipmentService } from 'src/app/services/calibration-equipment.service';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CalibrationEquipmentDialogComponent } from 'src/app/modals/calibration-equipment-dialog/calibration-equipment-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { EquipmentStatus } from 'src/app/enums/equipmentStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { UserService } from 'src/app/services/user.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { CalibrationFrequency } from 'src/app/enums/calibrationFrequency.enum';
import { Lookup } from 'src/app/models/lookup.model';
import { Router } from '@angular/router';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { TranslateService } from 'src/app/services/translate.service';
import { splitCamelCase } from 'src/app/_helpers/format-text';
@Component({
  standalone: false,
  selector: 'app-calibration-equipment',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
})
export class CalibrationEquipmentComponent extends CoreGridComponent<CalibrationEquipment> implements OnInit, AfterViewInit {

  organizationLevels: Lookup[] = [];
  users: Lookup[] = [];

  constructor(
    calibrationEquipmentService: CalibrationEquipmentService,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    private dateFormatService: DateFormatService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService,
    private organizationLevelService: OrganizationLevelService,
    private router: Router,
    private translateService: TranslateService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = calibrationEquipmentService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.CalibrationEquipment;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  get hasOrgFilter(): boolean {
    return true;
  }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<CalibrationEquipmentComponent>, null);
    super.ngOnInit();
    forkJoin({
      organizationLevels: this.organizationLevelService.getAll(),
      users: this.userService.getAll()
    }).subscribe(({ organizationLevels, users }) => {
      this.organizationLevels = organizationLevels;
      this.users = users;
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.modelNumber),
        displayName: 'Model Number',
        cellTemplate: (entity) => entity.modelNumber || '-',
        onClick: (entity) => this.openCalibrationDetail(entity),
        cssClass: () => 'cursor-pointer',
        tooltip: (entity: CalibrationEquipment) => `Click to view details for ${entity.equipmentCode}`
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.serialNumber),
        displayName: 'Serial Number',
        cellTemplate: (entity) => entity.serialNumber || '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.equipmentCode),
        displayName: 'Equipment Code',
        cellTemplate: (entity) => entity.equipmentCode || '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.manufacturer),
        displayName: 'Manufacturer',
        cellTemplate: (entity) => entity.manufacturer || '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.dateAcquired),
        displayName: 'Date Acquired',
        cellTemplate: (entity) => entity.dateAcquired ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(entity.dateAcquired, this.dateFormatService.userDateFormat) : '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.calibrationDue),
        displayName: 'Calibration Due',
        cellTemplate: (entity) => entity.calibrationDue ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(entity.calibrationDue, this.dateFormatService.userDateFormat) : '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.lastCalibrated),
        displayName: 'Last Calibrated',
        cellTemplate: (entity) => entity.lastCalibrated ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(entity.lastCalibrated, this.dateFormatService.userDateFormat) : '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.calibrationFrequency),
        displayName: 'Calibration Frequency',
        cellTemplate: (e) => this.translateService?.getDynamicTranslation(CalibrationFrequency[e.calibrationFrequency] || '-') ?? '-',
        enumFilterMap: this.enumToValueNameArray(CalibrationFrequency)
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.status),
        displayName: 'Status',
        cellTemplate: (e) => e.status != null ? this.translateService.getDynamicTranslation(splitCamelCase(EquipmentStatus[e.status])) : '-',
        enumFilterMap: this.enumToValueNameArray(EquipmentStatus)
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (e) => this.organizationLevels?.find(org => org.id === e.organizationLevelId)?.name || '-'
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.sampleType),
        displayName: 'Agent Type',
        cellTemplate: (e) => e.sampleType != null ? this.translateService.getDynamicTranslation(SampleTypeEnum[e.sampleType]) : '-',
        enumFilterMap: this.enumToValueNameArray(SampleTypeEnum)
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (e) => this.users?.find(u => u.id.toString() === e.createdByUserId)?.name || '-'
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: nameOf<CalibrationEquipment>(x => x.notifyBeforeDueDays),
        displayName: 'Notify Before Due Days',
        cellTemplate: (e) => e.notifyBeforeDueDays != null ? e.notifyBeforeDueDays.toString() : '-',
      }),
      new ColumnConfig<CalibrationEquipment>({
        name: 'owners',
        displayName: 'Equipment Owners',
        cellTemplate: (e) => e.owners?.length ? e.owners.map(o => o.ownerFullName || o.ownerUserName).join(', ') : '-',
      }),
      { name: 'action', displayName: 'Action' }
    ]
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<CalibrationEquipment>(x => x.organizationLevelId): return this.organizationLevelService;
      case nameOf<CalibrationEquipment>(x => x.createdByUserId): return this.userService;
      default: return undefined;
    }
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = []
  }

  override initializeTabs(): { routerLink: string; title: string }[] {
    const currentUser = this.authService.getCurrentUser();
    return getFilteredDevicesRouteConfig(currentUser.organizationLevelPermissions);
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Calibration Equipment';
    this.searchPlaceholder = 'Search Calibration Equipment';
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(entityId?: string): void {
    const calibrationEquipmentModel: IFormDialogData = {
      id: entityId
    };

    this.openDialog(CalibrationEquipmentDialogComponent, calibrationEquipmentModel);
  }

  override openEntityDeleteDialog(entity: CalibrationEquipment): void {
    return;
  }

  openCalibrationDetail(item: CalibrationEquipment) {
    this.router.navigate([`/dashboard/calibration-details/${item.id}`]);
  }
}
